import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Programme } from '@tariff/authoring';
import {
  api, type AccommodationDto, type InquiryDto, type InquiryMailDto,
} from '../lib/api';
import { money, stampDate, toDay, toIso } from '../lib/format';
import { useT, type MessageKey, type TFunction } from '../lib/i18n';
import { ROLES } from './Guests';

/**
 * The enquiries an operator has to answer.
 *
 * An enquiry is not a booking: nothing is held, no price is promised, and the
 * park replies by hand. That makes this list the whole of the workflow — an
 * enquiry nobody can see is a guest who wrote to the park and got no answer,
 * which is the product not working rather than a missing screen.
 *
 * ---------------------------------------------------------------------------
 * WHAT IS SHOWN, AND WHERE IT IS NOT
 * ---------------------------------------------------------------------------
 *
 * A guest's name, email, phone, address and licence plate are all here, in
 * plain sight, because the operator has to answer the person. None of it goes
 * anywhere else: not into a URL, not into a query string, not into a `mailto:`
 * — the enquiry is selected in memory, so a screenshot of the address bar, a
 * pasted link or a server log cannot carry somebody's details out of this room.
 *
 * The status is the only thing this screen writes. The record itself is what
 * the guest wrote and is never edited here.
 */

type Run = <T>(fn: () => Promise<T>, ok?: string | ((out: T) => string)) => Promise<T | null>;

export interface InquiriesProps {
  accommodations: AccommodationDto[];
  run: Run;
  busy?: boolean;
}

/**
 * The statuses this screen may SET — now the whole vocabulary.
 *
 * This list used to be shorter than what the database could hold: imported
 * history carried `read` and `closed` and nothing could write them back, so one
 * click moved a row somewhere it could never return from. The API accepts both
 * now, which is the fix; this is in the order reception moves through them.
 */
const SETTABLE = [
  'new', 'read', 'answered', 'booked', 'declined', 'closed', 'spam',
] as const;

/** Channel roles, in the operator's language. A stored party may be keyed by
    them rather than by the card's own categories; see `partyLabel`. */
const ROLE_LABEL = new Map<string, MessageKey>(ROLES);

/**
 * What was free when the guest asked, in words.
 *
 * `freeUnits` where the row has one — it is the fact, and "1 left" is worth
 * knowing when deciding which enquiry to answer first. `available` for rows
 * written before the count was recorded, where the boolean is all there is.
 */
function availabilityLabel(
  inquiry: { available?: boolean; freeUnits?: number | null }, t: TFunction,
): string {
  const free = inquiry.freeUnits;
  if (free === null || free === undefined) {
    return inquiry.available ? t('inquiries.wasFree') : t('inquiries.notCounted');
  }
  return free > 0 ? t('inquiries.wereFree', { count: free }) : t('inquiries.wasNotFree');
}

const STATUS_LABEL: Record<string, MessageKey> = {
  new: 'inquiries.statusNew',
  read: 'inquiries.statusRead',
  answered: 'inquiries.statusAnswered',
  booked: 'inquiries.statusBooked',
  declined: 'inquiries.statusDeclined',
  closed: 'inquiries.statusClosed',
  spam: 'inquiries.statusSpam',
};

function statusText(t: TFunction, status: string | undefined): string {
  const key = STATUS_LABEL[status ?? 'new'];
  return key ? t(key) : (status ?? '');
}

export function Inquiries({ accommodations, run, busy }: InquiriesProps) {
  const t = useT();
  const [inquiries, setInquiries] = useState<InquiryDto[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [filter, setFilter] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  /* The programme that priced the open enquiry, purely to put the park's own
     words on the party — "2 Volwassene" rather than "2 volwassene". */
  const [pricedBy, setPricedBy] = useState<{ id: string; programme: Programme } | null>(null);

  const reload = useCallback(async () => {
    const list = await api.inquiries();
    setInquiries(list);
    setLoaded(true);
    return list;
  }, []);

  useEffect(() => { void run(reload); }, [run, reload]);

  const open = inquiries.find((i) => i.id === openId) ?? null;

  /* Fetched when an enquiry is opened, not per row: the list would otherwise
     make one request per enquiry to label a party nobody has looked at yet. */
  useEffect(() => {
    if (!open?.pricedBy) { setPricedBy(null); return; }
    if (pricedBy?.id === open.pricedBy) return;
    void api.version(open.pricedBy)
      .then((v) => setPricedBy({ id: v.id, programme: v.programme }))
      .catch(() => setPricedBy(null));
  }, [open?.pricedBy, pricedBy?.id]);

  const shown = useMemo(
    () => (filter ? inquiries.filter((i) => (i.status ?? 'new') === filter) : inquiries),
    [inquiries, filter],
  );
  const newCount = inquiries.filter((i) => (i.status ?? 'new') === 'new').length;

  const label = (id: string) => accommodations.find((a) => a.id === id)?.label ?? id;

  const setStatus = (i: InquiryDto, status: string) => {
    void run(async () => {
      await api.setInquiryStatus(i.id, status);
      await reload();
      return true;
    }, t('app.inquiryStatusSet', { status: statusText(t, status) }));
  };

  return (
    <div className="stack">
      <section className="panel">
        <div className="panel-hd">
          <h2>{t('inquiries.heading')}</h2>
          <span className="grow" />
          <span className="note">
            {t('inquiries.count', { count: inquiries.length })}
            {newCount > 0 && <> {' · '}{t('inquiries.newCount', { count: newCount })}</>}
          </span>
          <div className="field" style={{ maxWidth: 170 }}>
            <label htmlFor="iq-status" className="sr">{t('inquiries.filterStatus')}</label>
            <select id="iq-status" value={filter} onChange={(e) => setFilter(e.target.value)}>
              <option value="">{t('inquiries.everyStatus')}</option>
              {SETTABLE.map((s) => (
                <option key={s} value={s}>{statusText(t, s)}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="panel-bd">
          <p className="note" style={{ marginBottom: 12 }}>{t('inquiries.intro')}</p>
          {!loaded ? (
            <p className="note">{t('inquiries.loading')}</p>
          ) : inquiries.length === 0 ? (
            <p className="note">{t('inquiries.empty')}</p>
          ) : shown.length === 0 ? (
            <p className="note">
              {t('inquiries.emptyForStatus', { status: statusText(t, filter) })}
            </p>
          ) : (
            <div className="scroll-x">
              <table>
                <thead>
                  <tr>
                    <th>{t('inquiries.columnGuest')}</th>
                    <th className="col-wide">{t('inquiries.columnAccommodation')}</th>
                    <th>{t('inquiries.columnStay')}</th>
                    <th className="col-wide r">{t('inquiries.columnPrice')}</th>
                    <th>{t('inquiries.columnStatus')}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {shown.map((i) => (
                    <tr key={i.id} className={i.id === openId ? 'picked' : undefined}>
                      <td>
                        {i.name}
                        <div className="meta">
                          {i.createdAt
                            // The park's day, not the first ten characters of a
                            // UTC instant: an enquiry submitted at 00:30 local is
                            // stored as 22:30 the day before, and truncating the
                            // text reports it as yesterday's.
                            ? t('inquiries.received', { date: stampDate(i.createdAt) })
                            : ''}
                        </div>
                      </td>
                      <td className="col-wide">{label(i.accommodationId)}</td>
                      <td className="num">
                        {i.arrival}
                        <div className="meta">{t('quote.nightCount', { count: i.nights })}</div>
                      </td>
                      <td className="col-wide r">
                        {i.price == null
                          ? <span className="muted">{t('inquiries.noPrice')}</span>
                          : money(i.price)}
                      </td>
                      <td>
                        <span className={`chip${(i.status ?? 'new') === 'new' ? ' tp' : ''}`}>
                          {statusText(t, i.status)}
                        </span>
                      </td>
                      <td className="r">
                        <button
                          type="button" className="btn"
                          onClick={() => setOpenId(i.id === openId ? null : i.id)}
                        >
                          {i.id === openId ? t('inquiries.close') : t('inquiries.open')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {open && (
        <Detail
          inquiry={open}
          accommodationLabel={label(open.accommodationId)}
          programme={pricedBy && pricedBy.id === open.pricedBy ? pricedBy.programme : null}
          busy={busy}
          onClose={() => setOpenId(null)}
          onStatus={(s) => setStatus(open, s)}
        />
      )}
    </div>
  );
}

function Detail({ inquiry, accommodationLabel, programme, onStatus, onClose, busy }: {
  inquiry: InquiryDto;
  accommodationLabel: string;
  programme: Programme | null;
  onStatus: (status: string) => void;
  onClose: () => void;
  busy?: boolean;
}) {
  const t = useT();
  const arrival = toDay(inquiry.arrival);
  const departure = arrival === null ? null : toIso(arrival + inquiry.nights);

  /*
    What the guest actually said, in words the person reading it uses.

    Three sources, in order, because a party is stored EXACTLY AS SENT and what
    that contains depends on the client:

      the park's own category  where the widget spoke the card's own keys, and
                               the card that priced this enquiry is still
                               around to say what it called them. Not ours to
                               translate: it is what the park typed.
      the channel role         where the widget spoke `adult`/`child`/`infant`,
                               which is what a picker sends and what the server
                               now maps for pricing while storing the original.
                               Those are OURS, so they translate.
      the raw key              anything else, shown rather than swallowed.

    The roles are kept apart rather than resolved through the card, on purpose:
    this park bills adults and children into one category, so resolving would
    show "2 × Gasten · 1 × Gasten" and lose the very distinction reception
    needs. Babies are free and priced at nothing, and they are on this screen
    for exactly that reason — somebody has to put out a cot.
  */
  const partyLabel = (key: string) => {
    const own = programme?.quantities?.find((q) => q.key === key)?.label;
    if (own) return own;
    const role = ROLE_LABEL.get(key);
    return role ? t(role) : key;
  };

  /*
    The split the guest typed, where the row has one.

    Reported by the operator: the party is no longer broken out into adults and
    children. (His words are quoted verbatim in the API's own comment and in
    `booking.spec.ts`; this file is guarded against Dutch, and rightly, because
    Dutch appearing HERE would be a UI string in the wrong place.)

    `inquiry.party` is in the card's categories, and this park's camping card
    bills adults and children as one — so it can only ever say "2 Gasten vanaf
    3 jaar". `guestParty` is the same people in the roles the guest picked.

    The split is labelled through the ROLES ONLY, deliberately not through
    `partyLabel`. That helper prefers the card's own word for a key, and a card
    is free to name a merging category `adult` — on which "2 Gasten vanaf 3
    jaar, 1 Kind" would put the merging word back on the very number that
    exists to avoid it. The card names categories; it does not name roles.

    Null on every enquiry taken before it was stored; those still fall back to
    the card's own words, which is all there is.
  */
  const split = inquiry.guestParty && Object.keys(inquiry.guestParty).length
    ? inquiry.guestParty : null;
  const party: Array<[string, number, string]> = split
    ? Object.entries(split).filter(([, n]) => n > 0)
      .map(([k, n]) => [k, n, ROLE_LABEL.has(k) ? t(ROLE_LABEL.get(k)!) : k])
    : Object.entries(inquiry.party).filter(([, n]) => n > 0)
      .map(([k, n]) => [k, n, partyLabel(k)]);
  const mismatch = inquiry.price != null && inquiry.quotedPrice != null
    && Math.abs(inquiry.price - inquiry.quotedPrice) > 0.005;

  const address = [
    // The addition is part of the door, not decoration: "12" and "12 bis" are
    // two different houses, and this is the field somebody retypes into a
    // booking system.
    [inquiry.street, inquiry.houseNumber, inquiry.houseNumberAddition]
      .filter(Boolean).join(' '),
    [inquiry.postalCode, inquiry.city].filter(Boolean).join('  '),
    inquiry.country,
  ].filter((line) => line && line.trim()).join(', ');

  return (
    <section className="panel">
      <div className="panel-hd">
        <h2>{t('inquiries.detailHeading', { name: inquiry.name })}</h2>
        <span className="grow" />
        <div className="field" style={{ maxWidth: 180 }}>
          <label htmlFor="iq-set" className="sr">{t('inquiries.setStatus')}</label>
          <select
            id="iq-set" value="" disabled={busy}
            onChange={(e) => { if (e.target.value) onStatus(e.target.value); }}
          >
            <option value="">{t('inquiries.setStatus')}</option>
            {SETTABLE.map((s) => (
              <option key={s} value={s}>{statusText(t, s)}</option>
            ))}
          </select>
        </div>
        <button type="button" className="btn" onClick={onClose}>{t('inquiries.close')}</button>
      </div>
      <div className="panel-bd">
        <div className="cols">
          <div className="stack">
            <div>
              <h3 className="eyebrow">{t('inquiries.guestHeading')}</h3>
              <table>
                <tbody>
                  <Row label={t('inquiries.name')} value={inquiry.name} />
                  <Row label={t('inquiries.email')} value={inquiry.email} />
                  <Row label={t('inquiries.phone')} value={inquiry.phone} />
                  <Row label={t('inquiries.address')} value={address} />
                  <Row label={t('inquiries.licencePlate')} value={inquiry.licencePlate} />
                </tbody>
              </table>
              {inquiry.comment && (
                <>
                  <h3 className="eyebrow" style={{ marginTop: 14 }}>
                    {t('inquiries.commentHeading')}
                  </h3>
                  <p className="note">{inquiry.comment}</p>
                </>
              )}
            </div>
          </div>

          <div className="stack">
            <div>
              <h3 className="eyebrow">{t('inquiries.stayHeading')}</h3>
              <table>
                <tbody>
                  <Row label={t('inquiries.accommodation')} value={accommodationLabel} />
                  <Row label={t('inquiries.arrival')} value={inquiry.arrival} />
                  <Row label={t('inquiries.departure')} value={departure} />
                  <Row
                    label={t('inquiries.nights')}
                    value={t('quote.nightCount', { count: inquiry.nights })}
                  />
                  <Row
                    label={t('inquiries.party')}
                    value={party.length
                      ? party.map(([, n, label]) => `${n} × ${label}`).join(' · ')
                      : null}
                  />
                </tbody>
              </table>

              <h3 className="eyebrow" style={{ marginTop: 14 }}>{t('inquiries.priceHeading')}</h3>
              <table>
                <tbody>
                  <Row
                    label={t('inquiries.priceComputed')}
                    value={inquiry.price == null ? null : money(inquiry.price)}
                  />
                  <Row
                    label={t('inquiries.priceQuoted')}
                    value={inquiry.quotedPrice == null ? null : money(inquiry.quotedPrice)}
                  />
                  <Row label={t('inquiries.pricedBy')} value={inquiry.pricedBy} />
                  {/*
                    Three answers, because a boolean had only two and the
                    missing one was the campsite's.

                    A park that does not enumerate its pitches has no count to
                    give, and folding that into "none were free" filed every
                    camping enquiry as a park that was full — which is the one
                    reading that makes reception not bother replying.
                  */}
                  <Row
                    label={t('inquiries.availability')}
                    value={availabilityLabel(inquiry, t)}
                  />
                </tbody>
              </table>
              {inquiry.price == null && (
                <p className="note" style={{ marginTop: 8 }}>{t('inquiries.priceUnknown')}</p>
              )}
              {mismatch && (
                <div className="diag" style={{ marginTop: 10 }}>
                  <div className="dg warning">
                    <span className="ic" aria-hidden="true">!</span>
                    <span>{t('inquiries.priceMismatch')}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <SentMails inquiry={inquiry} />

        <p className="note" style={{ marginTop: 16 }}>
          {t('inquiries.provenance', {
            channel: inquiry.channel ?? '',
            mail: inquiry.mailStatus ?? t('inquiries.noMail'),
            address: inquiry.remoteAddress ?? t('inquiries.notGiven'),
          })}
        </p>
        <p className="note">{t('inquiries.privacyNote')}</p>
      </div>
    </section>
  );
}

/**
 * The guest's own page for this enquiry, and the mails the park sent about it.
 *
 * ---------------------------------------------------------------------------
 * WHY THE MAILS ARE STORED AND NOT REBUILT
 * ---------------------------------------------------------------------------
 *
 * Rendering them again from the row would be cheaper and would answer a
 * different question. The tariff moves, the templates move, the park's
 * language setting moves — so "what would this enquiry's mail look like today"
 * is not "what did we actually say to this person". When a guest writes back
 * quoting a sentence, only the second one settles it.
 *
 * Fetched on demand rather than with the list: it is a body of text per
 * enquiry, nobody reads it for most of them, and loading it for a queue of two
 * hundred to serve the one that gets opened is the wrong trade.
 */
function SentMails({ inquiry }: { inquiry: InquiryDto }) {
  const t = useT();
  const [mails, setMails] = useState<InquiryMailDto[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setMails(null);
    setFailed(false);
    api.inquiryMails(inquiry.id)
      .then((r) => { if (live) setMails(r.mails); })
      .catch(() => { if (live) setFailed(true); });
    // Cancelled by a flag rather than an AbortController because the client
    // does not take one; the cost of the late response is one ignored result.
    return () => { live = false; };
  }, [inquiry.id]);

  return (
    <div style={{ marginTop: 18 }}>
      <h3 className="eyebrow">{t('inquiries.mailsHeading')}</h3>

      {/*
        The guest's own page.

        The operator opens this to see exactly what the guest sees — which is
        the first thing worth checking when somebody rings up confused. It is
        a link and not a copyable token: the token belongs in this one URL and
        nowhere else.
      */}
      {inquiry.url
        ? (
          <p className="note">
            <a href={inquiry.url} target="_blank" rel="noreferrer noopener">
              {t('inquiries.guestPage')}
            </a>
          </p>
        )
        : <p className="note">{t('inquiries.guestPageUnset')}</p>}

      {failed && <p className="note">{t('inquiries.mailsFailed')}</p>}
      {!failed && mails === null && <p className="note">{t('inquiries.mailsLoading')}</p>}
      {mails?.length === 0 && <p className="note">{t('inquiries.mailsNone')}</p>}

      {mails?.map((m) => (
        <div key={m.id} style={{ marginTop: 8 }}>
          <button
            type="button"
            className="btn"
            aria-expanded={open === m.id}
            onClick={() => setOpen(open === m.id ? null : m.id)}
          >
            {t(m.recipient === 'operator' ? 'inquiries.mailToPark' : 'inquiries.mailToGuest')}
            {' — '}
            {m.outcome === 'sent' ? t('inquiries.mailSent') : m.outcome}
          </button>
          {open === m.id && (
            <div style={{ marginTop: 8 }}>
              <table>
                <tbody>
                  <Row label={t('inquiries.mailTo')} value={m.toAddress} />
                  <Row label={t('inquiries.mailSubject')} value={m.subject} />
                  <Row label={t('inquiries.mailWhen')} value={stampDate(m.createdAt)} />
                </tbody>
              </table>
              {/*
                The plain-text half, in a `pre`.

                Not the HTML: rendering a stored message inside the panel would
                put mail markup into this document, and the text half carries
                every fact the HTML does — they are generated from one list of
                sections precisely so that is true.
              */}
              <pre className="mail-body">{m.bodyText}</pre>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/** A labelled fact, or the plain statement that the guest did not give one. */
function Row({ label, value }: { label: string; value?: string | null }) {
  const t = useT();
  return (
    <tr>
      <th style={{ width: '38%' }}>{label}</th>
      <td>{value ? value : <span className="muted">{t('inquiries.notGiven')}</span>}</td>
    </tr>
  );
}
